import React, { useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import settings from 'settings';
import { addDuration, getDurationString, parseDuration } from 'library/time';

import ExtendedSelect from 'components/extendedSelect/';
import TagSelector from 'components/tagSelector/';
import DurationPicker from 'components/durationPicker/duration-picker';
import {
  DatePicker, Form, Input, TimePicker
} from 'antd';
import { list } from 'actions';

import { getActivityLabel, getKey, getSelect } from 'library/helpers';
import { FormWrapper } from 'components/Page';
import REDUCER_KEY from 'reducers/reducer.keys';
import AvailableSignOnField from 'components/AvailableSignOnField';
import { isOta, parseAvailableSignOn, toLocalValue } from 'library/availableSignOn';
import { getOtPresets } from 'library/otPresets';

const timeFormat = 'HH:mm';
const dateFormat = 'DD-MM-YYYY HH:mm';
const FormItem = Form.Item;
const { TextArea } = Input;

const mapStateToProps = state => ({
  activities: state.api.assignmentActivities.data.results,
  inFlightActivities: state.api.assignmentActivities.inFlight
});

const mapDispatchToProps = dispatch => ({
  loadActivities(searchText = '') {
    // Specify search params
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [{
      id: 'active',
      value: true
    }];
    if (searchText) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }

    dispatch(list(REDUCER_KEY.assignmentActivities.key, {
      page: 0,
      pageSize: settings.APP_CONFIG.dropListSize,
      filters,
      sorted
    }));
  }
});

const mapPropsToFields = (props) => {
  const { dataSource } = props;

  const {
    activity, notes, start_time, end_time, tags
  } = dataSource;
  // Get duration based on the start/end times of the
  // activity.
  const duration = getDurationString(start_time, end_time);

  return {
    activity: Form.createFormField({
      value: activity && activity.activity_type ? {
        key: activity.id,
        label: getActivityLabel(activity)
      } : getSelect(activity)
    }),
    start_time: Form.createFormField({ value: start_time }),
    end_time: Form.createFormField({ value: end_time }),
    duration: Form.createFormField({ value: duration }),
    notes: Form.createFormField({ value: notes }),
    tags: Form.createFormField({ value: tags })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const signOnValidator = (rule, value, callback) => {
  const { error } = parseAvailableSignOn(value, { requireHalfHour: true });
  callback(error ? error.message : undefined);
};

const durationValidator = (rule, value, callback) => {
  const duration = parseDuration(value);
  if (duration.days === 0 && duration.hours === 0 && duration.minutes < 15) {
    callback('Duration must be greater than 15 minutes');
  }
  else {
    callback();
  }
};

const FormInput = ({
  activities, form, dataSource, inputFormItemLayout, inFlightActivities, loadActivities, picker, showNotes, showAdminNotes, showSignOn, showTags, themeName
}) => {
  const { getFieldDecorator } = form;
  const {
    id, activity, duration, notes, admin_notes, start_time, end_time, tags, available_sign_on, available_sign_on_local
  } = dataSource;

  const setEndTime = (startTime, time) => {
    const endTime = addDuration(startTime, time);
    form.setFieldsValue({ end_time: endTime });
  };

  const handleStartTimeChange = (value) => {
    setEndTime(value, parseDuration(form.getFieldValue('duration')));
  };

  const handleDurationChange = (value) => {
    setEndTime(start_time, parseDuration(value));
  };

  // The activity chosen in the form (it may differ from the saved one).
  const chosen = form.getFieldValue('activity');
  const resolvedActivity = (chosen && activity && chosen.key === activity.id ? activity : null)
    || activities.find(a => chosen && a.id === chosen.key);
  // Remember the last successfully resolved activity so the sign-on row survives a search
  // filter that drops the chosen activity from the list (rc-form unmounts the field otherwise).
  const lastResolvedRef = useRef(null);
  if (resolvedActivity) lastResolvedRef.current = resolvedActivity;
  const chosenActivity = resolvedActivity
    || (lastResolvedRef.current && chosen && lastResolvedRef.current.id === chosen.key
      ? lastResolvedRef.current
      : null);

  return (
    <Form>
      <FormItem {...inputFormItemLayout} label="Activity">
        {getFieldDecorator('activity', {
          initialValue: activity,
          rules: [{
            required: true,
            message: 'Please select an Activity.'
          }]
        })(
          <ExtendedSelect
            placeholder="Select Activity"
            fetching={inFlightActivities}
            dataSource={activities}
            optionKey={getKey}
            optionLabel={getActivityLabel}
            onFocus={loadActivities}
            onSearch={loadActivities}
          />
        )}
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Start Time">
        {getFieldDecorator('start_time', {
          initialValue: start_time,
          rules: [{
            required: true,
            message: 'Please enter the Start Time.'
          }]
        })(
          picker === 'time'
            ? <TimePicker format={timeFormat} onChange={handleStartTimeChange} />
            : <DatePicker showTime={{ format: 'HH:mm' }} format={dateFormat} onChange={handleStartTimeChange} />
        )}
        {picker === 'time'
        && <span style={{ marginLeft: '10px' }}>{start_time ? `Week ${start_time.format('w')}, Day ${start_time.format('D')}` : ''}</span>}
      </FormItem>
      <FormItem {...inputFormItemLayout} label="End Time">
        {getFieldDecorator('end_time', {
          initialValue: end_time
        })(
          picker === 'time'
            ? <TimePicker format={timeFormat} disabled />
            : <DatePicker showTime={{ format: 'HH:mm' }} format={dateFormat} onChange={handleStartTimeChange} disabled />
        )}
        {picker === 'time'
        && <span style={{ marginLeft: '10px' }}>{end_time ? `Week ${end_time.format('w')}, Day ${end_time.format('D')}` : ''}</span>}
      </FormItem>
      <FormItem {...inputFormItemLayout} label="Duration">
        {getFieldDecorator('duration', {
          initialValue: duration,
          rules: [
            {
              required: true,
              message: 'Please enter the Duration.'
            },
            { validator: durationValidator }
          ]
        })(
          <DurationPicker onChange={handleDurationChange} />
        )}
      </FormItem>
      {showSignOn && isOta(chosenActivity) && (
        <FormItem {...inputFormItemLayout} label="Available sign-on">
          {getFieldDecorator('available_sign_on_local', {
            // eslint-disable-next-line no-nested-ternary
            initialValue: 'available_sign_on_local' in dataSource ? available_sign_on_local : (isOta(activity) ? toLocalValue(available_sign_on || null, start_time) : null),
            preserve: true,
            rules: [{ validator: signOnValidator }]
          })(
            <AvailableSignOnField presets={getOtPresets()} anchor={start_time} showError={false} />
          )}
        </FormItem>
      )}
      {id > 0 && showTags && (
        <FormItem {...inputFormItemLayout} label="Tags">
          {getFieldDecorator('tags', {
            initialValue: tags
          })(
            <TagSelector
              theme={themeName}
              placeholder="Select Tags"
              tagSelectorReducerKey={REDUCER_KEY.assignmentCollectionValues}
              maxTagCount={3}
            />
          )}
        </FormItem>
      )}
      {showNotes && (
        <FormItem {...inputFormItemLayout} label="Notes">
          {getFieldDecorator('notes', {
            initialValue: notes
          })(
            <TextArea maxLength={5000} rows={4} />
          )}
        </FormItem>
      )}
      {showAdminNotes && (
        <FormItem {...inputFormItemLayout} label="Admin Notes">
          {getFieldDecorator('admin_notes', {
            initialValue: admin_notes
          })(
            <TextArea maxLength={5000} rows={4} />
          )}
        </FormItem>
      )}
    </Form>
  );
};

FormInput.propTypes = {
  activities: PropTypes.array,
  inFlightActivities: PropTypes.bool,
  form: PropTypes.object,
  dataSource: PropTypes.object,
  onFieldsChange: PropTypes.func,
  onValuesChange: PropTypes.func,
  loadActivities: PropTypes.func,
  picker: PropTypes.oneOf(['time', 'datetime']),
  showNotes: PropTypes.bool,
  showAdminNotes: PropTypes.bool,
  showSignOn: PropTypes.bool,
  showTags: PropTypes.bool,
  themeName: PropTypes.string,
  inputFormItemLayout: PropTypes.object
};

FormInput.defaultProps = {
  activities: [],
  inFlightActivities: false,
  form: {},
  dataSource: {},
  onFieldsChange() {
  },
  onValuesChange() {
  },
  loadActivities() {
  },
  picker: 'time',
  showNotes: true,
  showAdminNotes: false,
  showSignOn: false,
  showTags: true,
  themeName: 'assignment',
  inputFormItemLayout: {}
};

const ActivityInput = FormWrapper(Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange
})(FormInput));

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(ActivityInput);
